import React, { useState } from "react";
import { httpService } from "../../../base/services/httpService.service";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input, Col, Select, Row, notification, Tabs, Upload } from "antd";
import { getLanguageIdByName, getLocationIdByName, getWorkspaceIdByName } from "../helpers";
import icon from "../../../assets/images/uploadDraggerIcon.png";
import { LocalStorageService } from "../../../base/services/local-storage.service";
import { CountriesEnum, ExcelUserRolesEnum, GenderEnum, LanguagesEnum } from "../../../utils/enums";
import { genderOptions } from "../../../utils/options";
import { read, utils } from "xlsx";
import BasicModal from "../../../ui/BasicModal/BasicModal";
import NewMemberTooltip from "../components/user-management/components/NewMemberTooltip/NewMemberTooltip";

import styles from "./UserManagement.module.scss";

const { Option } = Select;
const { Dragger } = Upload;

function NewMember({
   rolesList,
   languages,
   countries,
   workspaces,
   record,
   visible,
   onCancel,
   adminUserManagementPage = false,
   isEnumeratorsPage = false,
   downloadRole,
   fetchTableData = () => {}
}) {
   const currentWorkspace = LocalStorageService.getItem("currentWorkspaceId");

   const [form] = Form.useForm();
   const navigate = useNavigate();
   const [load, setLoad] = useState(false);
   const [fileList, setFileList] = useState([]);
   const [membersData, setMembersData] = useState([]);
   const [selectedWorkspaces, setSelectedWorkspaces] = useState(
      currentWorkspace ? [currentWorkspace] : []
   );

   const createOrUpdateMember = (api, values) => {
      const editApi = api === "edit";
      form.setFieldsValue(values);

      httpService.post(
         `/Members/${editApi ? "edit" : "add"}`,
         {
            fullName: values.username,
            email: values.email,
            roleId:
               values.role === "Data Collector" ? ExcelUserRolesEnum["DataCollector"] : values.role,
            workspaceIds:
               isEnumeratorsPage || !adminUserManagementPage
                  ? [LocalStorageService.getItem("currentWorkspaceId")]
                  : getWorkspaceIdByName(workspaces, values),
            ...(isEnumeratorsPage || !adminUserManagementPage
               ? { projectId: LocalStorageService.getItem("currentProjectID") }
               : {}),
            languages: getLanguageIdByName(languages, values),
            locationId: getLocationIdByName(countries, values),
            gender: values.gender,
            id: editApi ? record.key : undefined
         },
         (res) => {
            if (res?.isSuccess) {
               notification.open({
                  type: "success",
                  message: `${
                     isEnumeratorsPage ? "Data Collector" : "Member"
                  }  has been added successfully.`
               });
               fetchTableData();
               if (!isEnumeratorsPage && adminUserManagementPage)
                  navigate("/admin-user-management");
               setLoad(false);
               onCancel("newMemberModal");
               form.resetFields();
            }
         },
         (error) => {
            setLoad(false);
            form.setFieldsValue(values);
            if (error.response && error.response.data && error.response.data.errors) {
               const errorMessage = error.response.data.errors[0];
               notification.error({
                  type: "error",
                  message: errorMessage
               });
            } else {
               notification.error({
                  type: "error",
                  message: `Something went wrong`
               });
            }
         }
      );
   };

   const onFinish = (values) => {
      setLoad(true);
      if (!record) {
         createOrUpdateMember("add", values);
      } else {
         createOrUpdateMember("edit", values);
      }
   };

   const handleFileUpload = ({ file }) => {
      const reader = new FileReader();
      reader.onload = (e) => {
         const data = new Uint8Array(e.target.result);
         const workbook = read(data, { type: "array" });

         // Process sheets
         const membersSheet = workbook.Sheets["Members"];
         const rolesSheet = workbook.Sheets["Roles"];

         const membersData = utils.sheet_to_json(membersSheet);
         const rolesData = utils.sheet_to_json(rolesSheet);

         // Extract valid roles
         const validRoles = rolesData.map((role) => role.Role);

         const roleIdMapping = ExcelUserRolesEnum;

         const locationIdMapping = CountriesEnum;

         const languagesMapping = LanguagesEnum;

         const validLanguagesIds = Object.values(languagesMapping);
         const validCountries = Object.keys(locationIdMapping);

         const validatedMembers = membersData
            .map((member) => {
               const memberLocation = member.Location.replaceAll(" ", "_").toUpperCase();
               if (!validRoles.includes(member.Role)) {
                  console.log(`Invalid role for member: ${member["FullName"]}`);
                  return null;
               }

               // Example: Lang1, Lang2, Lang3
               const memberLanguagesIds = member.Language.split(", ").map(
                  (lang) => languagesMapping[lang.trim().toUpperCase()]
               );

               const invalidLangIds = memberLanguagesIds.filter(
                  (langId) => !validLanguagesIds.includes(langId)
               );

               if (invalidLangIds.length > 0) {
                  console.log(
                     `Invalid language(s) for member: ${member["FullName"]}: ${invalidLangIds.join(
                        ", "
                     )}`
                  );
                  return null;
               }

               if (!validCountries.includes(memberLocation)) {
                  console.log(`Invalid location for member: ${member["FullName"]}`);
                  return null;
               }

               return {
                  fullName: member.FullName,
                  email: member.Email,
                  gender: GenderEnum[member.Gender],
                  roleId: roleIdMapping[member.Role],
                  locationId: locationIdMapping[memberLocation],
                  languages: memberLanguagesIds,
                  projectId: LocalStorageService.getItem("currentProjectID")
               };
            })
            .filter((member) => member !== null);

         setMembersData(validatedMembers);
      };

      reader.readAsArrayBuffer(file);
   };

   const handleCreateMultipleMembers = () => {
      setLoad(true);

      httpService.post(
         "/Members/addMembers",
         membersData.map((member) => ({
            ...member,
            workspaceIds: selectedWorkspaces
         })),
         (res) => {
            if (res?.isSuccess) {
               notification.open({
                  type: "success",
                  message: "Members have been added successfully."
               });

               fetchTableData();
               onCancel("newMemberModal");
               form.resetFields();
            }

            setLoad(false);
         },
         (error) => {
            setLoad(false);
            if (error.response && error.response.data && error.response.data.errors) {
               const errorMessage = error.response.data.errors[0];
               notification.error({
                  type: "error",
                  message: errorMessage
               });
            } else {
               notification.error({
                  type: "error",
                  message: `Something went wrong`
               });
            }
         }
      );
   };

   const items = [
      {
         key: "1",
         label: "Add one member",
         children: (
            <Form
               form={form}
               labelCol={{ span: 24 }}
               wrapperCol={{ span: 24 }}
               name="basic"
               onFinish={onFinish}
               initialValues={{ role: isEnumeratorsPage ? "Data Collector" : null }}
            >
               {/* First Row */}
               <h3 className={styles.newMemberHeader}>Personal information</h3>
               <Row gutter={24}>
                  <Col span={12}>
                     <Form.Item
                        label="Full name*"
                        name="username"
                        rules={[
                           {
                              required: true,
                              message: "For our records, please input your full legal name"
                           }
                        ]}
                     >
                        <Input autoComplete="off" placeholder="Enter a name" />
                     </Form.Item>
                  </Col>
                  <Col span={12}>
                     <Form.Item
                        label="Email*"
                        name="email"
                        rules={[
                           {
                              required: true,
                              message: "Please enter an email"
                           },
                           {
                              pattern: /^[a-zA-Z][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                              message: "Please enter a valid email address"
                           }
                        ]}
                     >
                        <Input autoComplete="off" placeholder="Enter an email" />
                     </Form.Item>
                  </Col>
                  <Col span={12}>
                     <Form.Item
                        label="Language*"
                        name="language"
                        rules={[
                           {
                              required: true,
                              message:
                                 "Kindly specify your language preference for a tailored experience."
                           }
                        ]}
                     >
                        <Select
                           mode="multiple"
                           showSearch
                           allowClear
                           placeholder="--Select an option--"
                           optionFilterProp="children"
                           filterOption={(input, option) =>
                              option.children.toLowerCase().startsWith(input.toLowerCase())
                           }
                           style={{ width: "100%" }}
                        >
                           {languages.map((option, i) => (
                              <Option value={option.label} key={i}>
                                 {option.label}
                              </Option>
                           ))}
                        </Select>
                     </Form.Item>
                  </Col>
                  <Col span={12}>
                     <Form.Item
                        label="Gender*"
                        name="gender"
                        rules={[
                           {
                              required: true,
                              message: "For demographic analysis, please specify your gender."
                           }
                        ]}
                     >
                        <Select
                           showSearch
                           placeholder="--Select a Gender--"
                           allowClear
                           options={genderOptions}
                        />
                     </Form.Item>
                  </Col>
                  <Col span={12}>
                     <Form.Item
                        label="Location*"
                        name="location"
                        rules={[
                           {
                              required: true,
                              message:
                                 "For accurate information, please provide your current location."
                           }
                        ]}
                     >
                        <Select
                           showSearch
                           placeholder="--Select a Location--"
                           allowClear
                           optionFilterProp="children"
                           filterOption={(input, option) =>
                              option.children.toLowerCase().startsWith(input.toLowerCase())
                           }
                           style={{ width: "100%" }}
                        >
                           {countries.map((option, i) => (
                              <Option value={option.label} key={i}>
                                 {option.label}
                              </Option>
                           ))}
                        </Select>
                     </Form.Item>
                  </Col>
                  {!isEnumeratorsPage && adminUserManagementPage && (
                     <Col span={12}>
                        <Form.Item
                           label="Select Workspaces*"
                           name="workspace"
                           rules={[
                              {
                                 required: true,
                                 message:
                                    "For organizational purposes, please indicate your workspace"
                              }
                           ]}
                        >
                           <Select
                              placeholder="--Select Workspaces--"
                              mode="multiple"
                              allowClear
                              showSearch
                              // value={initialValues.workspace}
                           >
                              {workspaces.map((option, i) => {
                                 return (
                                    <Option value={option.name} key={i}>
                                       {option.name}{" "}
                                    </Option>
                                 );
                              })}
                           </Select>
                        </Form.Item>
                     </Col>
                  )}
                  <Col span={12}>
                     <Form.Item
                        label="Select Role*"
                        name="role"
                        rules={[
                           {
                              required: isEnumeratorsPage ? false : true,
                              message: "Specify your role or position within the organization."
                           }
                        ]}
                     >
                        <Select
                           placeholder="--Select a Role--"
                           showSearch
                           disabled={isEnumeratorsPage}
                           options={rolesList.map((option) => ({
                              value: option.id,
                              label: option.name
                           }))}
                        />
                     </Form.Item>
                  </Col>
                  <Col span={24}>
                     <div
                        className={styles.buttonsDiv}
                        style={{
                           marginTop: "24px"
                        }}
                     >
                        <Button
                           onClick={() => onCancel("newMemberModal")}
                           className="defaultButton"
                        >
                           Cancel
                        </Button>

                        <Form.Item>
                           <Button
                              htmlType="submit"
                              className="primaryButton"
                              disabled={load}
                              loading={load}
                              style={{
                                 height: "44px"
                              }}
                           >
                              {load ? <>Processing</> : !record ? "Add new member" : "Edit member"}
                           </Button>
                        </Form.Item>
                     </div>
                  </Col>
               </Row>
            </Form>
         )
      },
      {
         key: "2",
         label: "Add multiple members",
         disabled: false,
         children: (
            <div>
               {/* Content of Tab 2 (addMultiple) */}
               <div className={styles.addMultipleDiv}>
                  <div className={styles.addMultipleText}>
                     To add multiple members at once, fill out this format and upload it into the
                     box below
                  </div>
                  <Button className={styles.addMultipleButton}>
                     <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="17"
                        viewBox="0 0 14 17"
                        fill="none"
                     >
                        <path
                           d="M9.28578 10.0578L7.00007 12.3435M7.00007 12.3435L4.71436 10.0578M7.00007 12.3435V6.05776M13.2858 15.2006C13.2858 15.5037 13.1653 15.7944 12.951 16.0087C12.7368 16.223 12.446 16.3435 12.1429 16.3435H1.85721C1.5541 16.3435 1.26342 16.223 1.04909 16.0087C0.834767 15.7944 0.714355 15.5037 0.714355 15.2006V2.62919C0.714355 2.32608 0.834767 2.03539 1.04909 1.82106C1.26342 1.60674 1.5541 1.48633 1.85721 1.48633H9.28578L13.2858 5.48633V15.2006Z"
                           stroke="white"
                           strokeLinecap="round"
                           strokeLinejoin="round"
                        />
                     </svg>
                     <a
                        download={
                           downloadRole === "admin" ? "Multiple_User_Members" : "Multiple_Members"
                        }
                        href={`${process.env.REACT_APP_PUBLIC_STORAGE}/${
                           downloadRole === "admin"
                              ? "Multiple_User_Members.xlsx"
                              : "Multiple_Members.xlsx"
                        }`}
                     >
                        Download Format
                     </a>
                  </Button>
               </div>
               {/* Add your form for uploading Excel file here */}
               <Dragger
                  style={{ background: "#F1F6FF", borderColor: "#B3BCEC" }}
                  accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  beforeUpload={(file) => {
                     setFileList([file]);
                     handleFileUpload({ file });
                     return false;
                  }}
                  fileList={fileList}
                  onRemove={() => setFileList([])}
               >
                  <div className="ant-upload-drag-icon">
                     <center>
                        <div className={styles.uploadDraggerIcon}>
                           <img src={icon} alt="upload dragger icon" />
                        </div>
                     </center>
                  </div>
                  <p className={styles.uploadDraggerHeader}>
                     Drag and drop files here, or click to select files
                  </p>
                  <p className={styles.uploadDraggerText}>
                     Excel files only <br></br> Max file size: 1 MB
                  </p>
               </Dragger>

               {adminUserManagementPage && (
                  <Row style={{ marginTop: "20px" }} className="w-full flex-column">
                     <label>Select workspaces:</label>

                     <Select
                        className="w-50"
                        placeholder="--Select Workspaces--"
                        mode="multiple"
                        allowClear
                        showSearch
                        maxTagCount={2}
                        options={workspaces.map((option) => ({
                           value: option.id,
                           label: option.label
                        }))}
                        onChange={(value) => setSelectedWorkspaces(value)}
                     />
                  </Row>
               )}
               <Row gutter={24} className="mt-8">
                  <Col span={24}>
                     <div className={styles.buttonsDiv}>
                        <Button
                           onClick={() => onCancel("newMemberModal")}
                           className={styles.buttonCancel}
                        >
                           Cancel
                        </Button>

                        <Form.Item>
                           <Button
                              htmlType="submit"
                              className={styles.buttonsCreateProfile}
                              disabled={load}
                              loading={load}
                              onClick={handleCreateMultipleMembers}
                           >
                              {load ? <>Processing</> : !record ? "Create Profile" : "Edit Member"}
                           </Button>
                        </Form.Item>
                     </div>
                  </Col>
               </Row>
            </div>
         )
      }
   ];

   return (
      <BasicModal
         isModalOpen={visible}
         handleCancel={() => {
            form.resetFields();
            onCancel("newMemberModal");
         }}
         width={700}
         footerButtonCustomStyle
      >
         <Row gutter={16} align="middle" justify="center">
            <h1
               className="modal-heading"
               style={{
                  color: "#4158CF",
                  fontSize: "22px",
                  marginBottom: "0px"
               }}
            >
               Add new member
            </h1>
            <NewMemberTooltip
               title="Add new member"
               summary="Each user can have only one role per organization, and each email must be unique."
               details={[
                  {
                     label: "Guest",
                     text:
                        "This role provides basic, view-only access to specific project data, ideal for stakeholders needing quick insights without editing capabilities."
                  },
                  {
                     label: "Data Collector",
                     text:
                        "Responsible for directly gathering information in the field or remotely, accurately inputting responses into the platform."
                  },
                  {
                     label: "Junior Manager",
                     text: "Oversees daily data collection activities, add more."
                  },
                  {
                     label: "Project Manager",
                     text: "Manages all aspects of a specific project, add more."
                  },
                  {
                     label: "Senior Manager",
                     text: "Provides strategic oversight across multiple projects, add more."
                  }
               ]}
            />
         </Row>
         <Tabs defaultActiveKey="1" items={items} />
      </BasicModal>
   );
}

export { NewMember };
